import { useCallback } from 'react';
import { validate } from 'uuid';
import { useParams } from 'react-router';
import { useStore } from '30.quickConnect.Stores';
import { isListField } from '90.quickConnect.Models/guards';
import {
  ComboDesc,
  FieldDesc,
  HierarchicalDesc,
  HierarchicalChoice,
  Choice,
  RepeatableGroupDesc,
} from '90.quickConnect.Models/models';
import { choiceListParse } from '80.quickConnect.Core/helpers/choiceListParsers';
import { FieldType, SharedListType } from '90.quickConnect.Models/enums';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { flatten } from '80.quickConnect.Core/helpers';

export const useHydrateDefaultChoices = () => {
  const {
    SharedListStore: { getSharedList },
  } = useStore();

  const { declarationId } = useParams();

  const hasValidateListId = useCallback(
    (listId: string | undefined): listId is string => !!listId && validate(listId),
    [],
  );

  const setDefaultChoicesFromListId = useCallback(
    async (listId: string, f: ComboDesc | HierarchicalDesc): Promise<void> => {
      if (!f.defaultValue || declarationId) return;
      const sharedList = await getSharedList(listId);

      if (!sharedList) return;

      const listChoices =
        f.fieldType === FieldType.HierarchicalList
          ? flatten(sharedList.hierarchicalChoices, (h) => h.children)
          : sharedList.data;

      const newValues = choiceListParse(f.defaultValue, listChoices, sharedList.listType, CustomLogger.getInstance());

      if (!f.value || (Array.isArray(f.value) && f.value.length === 0))
        f.value =
          f.fieldType === FieldType.Combo || f.fieldType === FieldType.RadioList
            ? newValues.length === 1
              ? newValues.at(0)
              : undefined
            : newValues;
    },
    [declarationId, getSharedList],
  );

  const setDefaultChoicesFromListDef = useCallback(
    (listChoices: Choice[] | HierarchicalChoice[] | undefined, f: ComboDesc | HierarchicalDesc) => {
      if (!f.defaultValue || !listChoices) return;

      const sharedListType =
        f.fieldType === FieldType.HierarchicalList ? SharedListType.Hierarchical : SharedListType.Simple;

      const newValues = choiceListParse(f.defaultValue, listChoices, sharedListType, CustomLogger.getInstance());

      f.value =
        f.fieldType === FieldType.Combo || f.fieldType === FieldType.RadioList
          ? newValues.length === 1
            ? newValues.at(0)
            : undefined
          : newValues;
    },
    [],
  );

  const populateDefaultChoices = useCallback(
    async (fields: FieldDesc[]): Promise<void> => {
      await Promise.all(
        fields.map(async (f: FieldDesc) => {
          if (isListField(f)) {
            if (hasValidateListId(f.listId)) await setDefaultChoicesFromListId(f.listId, f);
            else setDefaultChoicesFromListDef(f.listChoice, f);
          }

          // Cas particulier : RepeatableGroup
          if (f.fieldType === FieldType.RepeatableGroup) {
            const rg = f as RepeatableGroupDesc;

            // On traite le template (ce qui servira pour les nouvelles lignes)
            if (rg.groupTemplate && rg.groupTemplate.length > 0) {
              await populateDefaultChoices(rg.groupTemplate);
            }

            // Si des lignes existent déjà (nbRowOnInit > 0), on traite aussi leurs items
            if ((rg.nbRowOnInit ?? 0) > 0 && Array.isArray(rg.items) && rg.items.length > 0) {
              await Promise.all(
                rg.items.map(async (row) => {
                  if (row.items && row.items.length > 0) {
                    await populateDefaultChoices(row.items);
                  }
                }),
              );
            }

            return;
          }

          // Autres champs : on descend simplement dans f.items si présent
          const { items } = f;

          if (items && items.length > 0) {
            await populateDefaultChoices(items);
          }
        }),
      );
    },
    [hasValidateListId, setDefaultChoicesFromListDef, setDefaultChoicesFromListId],
  );

  return { populateDefaultChoices };
};
